import { useState } from "react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Users,
  Building2,
  Banknote,
  Hourglass,
  Handshake,
  TrendingUp,
  Scale,
  Clock,
  HeartHandshake,
  KeyRound,
  Wrench,
  Hammer,
  ClipboardCheck,
  ShieldCheck,
} from "lucide-react";
import PartnerForm from "./AgentPartnerForm";

const TABS = [
  {
    id: "agent_partner",
    label: "Real Estate Agents",
    icon: Building2,
    headline: "Close the deals you couldn't before.",
    intro:
      "We bring you distressed-seller leads, investor-ready properties, and creative-financing structures that turn dead listings into commission checks. You stay the agent of record — we're the engine behind the scenes.",
    benefits: [
      { icon: Users, t: "More qualified leads — sent to you", d: "Homeowners we talk to who want a traditional listing instead of a cash sale? We refer them straight to our partner agents." },
      { icon: Building2, t: "Off-market inventory for investors", d: "Have buyers hunting fix-and-flip, rental, or wholesale deals? We surface motivated sellers and pre-foreclosure properties before they hit the MLS." },
      { icon: Banknote, t: "Creative financing that closes deals", d: "Subject-to, seller-finance, lease-options, wraps, novations — we structure terms that work when conventional lending fails." },
      { icon: Hourglass, t: "Move stale listings — fast", d: "Listing sitting 60, 90, 180+ days? We bring serious cash buyers, creative offers, and short-sale support to get the closing scheduled." },
      { icon: Handshake, t: "Co-brokered, co-credited, no poaching", d: "Your client stays your client. We split where appropriate, refer cleanly, and respect every existing listing agreement — in writing." },
      { icon: TrendingUp, t: "Surplus-funds referral splits", d: "Refer a former homeowner owed surplus funds — we recover the money and you earn a referral fee on every successful claim." },
    ],
  },
  {
    id: "probate_attorney",
    label: "Probate Attorneys",
    icon: Scale,
    headline: "Close estates faster — and give heirs better outcomes.",
    intro:
      "Probate cases stall when heirs can't agree, properties sit vacant, mortgages eat equity, and estate liquidity dries up. We give your firm a single-call solution for the real-estate side of every estate — clean, fast, and respectful of fiduciary duty.",
    benefits: [
      { icon: Clock, t: "Faster estate liquidity", d: "Cash offers in 7–21 days mean heirs get distributions sooner, your firm closes files faster, and the court gets clean accountings." },
      { icon: HeartHandshake, t: "Empathy with the family", d: "Every contact with grieving heirs is handled with discretion. No high-pressure tactics, no reputational risk to your firm." },
      { icon: ShieldCheck, t: "Title-clean, court-friendly closings", d: "We work with title companies experienced in probate. Court confirmation, sole-heir affidavits, ancillary probate — we know the workflow." },
      { icon: TrendingUp, t: "Recover heirs' surplus funds", d: "Estate already lost a home to foreclosure? We recover surplus proceeds the heirs are owed — extending value to your client without billable hours from you." },
      { icon: Banknote, t: "Creative options for upside-down estates", d: "When the mortgage exceeds value, we negotiate short sales or subject-to assumptions so the estate exits cleanly without a deficiency." },
      { icon: Handshake, t: "Reciprocal referrals", d: "We send our homeowners with title issues, contested deeds, or estate questions directly to your firm. You bill the work — we don't take a cut." },
    ],
  },
  {
    id: "property_manager",
    label: "Property Managers",
    icon: KeyRound,
    headline: "Turn your worst doors into cash — keep your good ones.",
    intro:
      "When an owner stops responding, falls behind on the mortgage, or wants out, you lose a managed door. We give your owners an exit, give you new acquisition leads from your own portfolio, and refer every property we acquire that needs management — straight to you.",
    benefits: [
      { icon: KeyRound, t: "Exits for tired or distressed owners", d: "Owner facing foreclosure, divorce, or burnout? Instead of losing the door to a competitor, refer them to us — get a one-time fee, keep the relationship goodwill." },
      { icon: Building2, t: "First right of refusal on our acquisitions", d: "Every property we acquire that becomes a long-term hold gets routed to you for management. New doors, no marketing spend." },
      { icon: ShieldCheck, t: "Clean handoffs for owner-financed deals", d: "When we structure subject-to or seller-finance, we frequently keep the original owner's PM. You keep collecting management fees on a deal that would otherwise have ended." },
      { icon: Banknote, t: "Buy-out options for problem properties", d: "Property losing money? Constant maintenance issues? Tenants from hell? We buy as-is — you walk away clean, owner walks away clean." },
      { icon: TrendingUp, t: "Investor leads for your management pipeline", d: "Our investor network needs PMs in every market we operate in. Strong-fit PMs get warm-introduced to active buyers." },
      { icon: Handshake, t: "No competition with your core service", d: "We don't manage — we acquire and refer. Zero conflict with your business model. Just upside." },
    ],
  },
  {
    id: "contractor",
    label: "Contractors",
    icon: Hammer,
    headline: "Steady scopes, fast pay, no chasing homeowners.",
    intro:
      "Working with retail homeowners means slow decisions, payment disputes, and one-off jobs. Working with us means a recurring pipeline of investor-funded rehabs with clear scopes, fast pay, and a single point of contact for every project.",
    benefits: [
      { icon: Wrench, t: "Recurring rehab pipeline", d: "We acquire properties every month that need cosmetic-to-full renovations. Vetted GCs and trades get first call." },
      { icon: ClipboardCheck, t: "Clear scopes, no scope creep", d: "We pre-walk every property and write the scope before bid. No 'while you're at it' surprises, no homeowner indecision." },
      { icon: Banknote, t: "Net-7 to net-15 pay terms", d: "Investor-funded projects don't wait on insurance checks or HELOCs. You bill, you get paid — fast, on draws or on completion." },
      { icon: Building2, t: "Volume pricing without volume risk", d: "We feed you steady jobs at fair rates instead of chasing one-time homeowners. Predictable revenue, predictable scheduling." },
      { icon: Users, t: "Direct retail referrals", d: "Homeowners we help keep their homes often need repairs (post-foreclosure cleanup, code violations, deferred maintenance). We refer them directly to our trusted contractors." },
      { icon: ShieldCheck, t: "One PM, one decision-maker", d: "No homeowner committees, no spousal disputes, no last-minute changes. One project manager per job — you talk to one person, get clear answers." },
    ],
  },
];

export default function Partners() {
  const [tab, setTab] = useState(TABS[0].id);

  return (
    <section
      id="agents"
      data-testid="partners-section"
      className="py-20 md:py-32 bg-[#0B1B3D] text-white relative overflow-hidden"
    >
      <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-[#C5A059]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-[#2C5545]/30 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <div className="mb-4">
            <span className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.24em] text-[#C5A059] font-medium">
              Partner With Clear Path
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight mb-5">
            Built for the people <em className="text-[#C5A059] not-italic font-medium">who close deals.</em>
          </h2>
          <p className="text-white/75 leading-relaxed text-base lg:text-lg">
            Real estate agents, probate attorneys, property managers, contractors —
            you each see distressed homeowners we can help, and we see deals you
            can profit from. Pick your role below to see exactly how we win
            together.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            data-testid="partners-tabs"
            className="bg-white/5 border border-white/10 p-1.5 h-auto flex flex-wrap gap-1 mb-10 rounded-lg"
          >
            {TABS.map((t) => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                data-testid={`partners-tab-${t.id}`}
                className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#0B1B3D] data-[state=inactive]:text-white/75 data-[state=inactive]:hover:text-white px-5 py-3 rounded-md text-sm font-medium flex items-center gap-2 transition-colors"
              >
                <t.icon className="h-4 w-4" />
                <span>{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {TABS.map((t) => (
            <TabsContent
              key={t.id}
              value={t.id}
              className="mt-0 focus-visible:outline-none"
              data-testid={`partners-content-${t.id}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7">
                  <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl mb-4 leading-tight">
                    {t.headline}
                  </h3>
                  <p className="text-white/70 leading-relaxed mb-8 max-w-2xl">
                    {t.intro}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {t.benefits.map((b, i) => (
                      <div
                        key={i}
                        data-testid={`partners-${t.id}-benefit-${i}`}
                        className="rounded-lg border border-white/15 bg-white/[0.04] backdrop-blur-sm p-6 hover:bg-white/[0.07] hover:border-[#C5A059]/30 transition-all"
                      >
                        <div className="h-10 w-10 rounded-md bg-[#C5A059]/15 border border-[#C5A059]/30 flex items-center justify-center mb-4">
                          <b.icon className="h-5 w-5 text-[#C5A059]" />
                        </div>
                        <h4 className="font-display text-lg text-white mb-2 leading-snug">
                          {b.t}
                        </h4>
                        <p className="text-sm text-white/65 leading-relaxed">
                          {b.d}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="lg:sticky lg:top-28">
                    <PartnerForm defaultPartnerType={t.id} />
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>

        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 pt-10 border-t border-white/15">
          <Stat k="48 hrs" v="Average response" />
          <Stat k="$0" v="Cost to apply or join" />
          <Stat k="50" v="States covered" />
          <Stat k="4" v="Partner channels" />
        </div>
      </div>
    </section>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="font-display text-3xl lg:text-4xl text-[#C5A059] leading-none">{k}</div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/60 mt-2">{v}</div>
    </div>
  );
}
